import { INJ_DENOM, NBZ_DENOM, SHU_DENOM, SOL_DENOM, USDC_NEW_DENOM, USDT_DENOM } from '@/constants/constants'
import { create } from 'zustand'
import { getTokenByDenom } from '@/services/token-service'

export const currenciesSettings = {
  minValue: {
    [INJ_DENOM]: 0.01,
    [NBZ_DENOM]: 10,
    [SHU_DENOM]: 1,
    [USDC_NEW_DENOM]: 0.1,
    [USDT_DENOM]: 0.1,
    [SOL_DENOM]: 0.001,
  },
  maxValue: {
    [INJ_DENOM]: 2,
    [NBZ_DENOM]: 1_000,
    [SHU_DENOM]: 100,
    [USDC_NEW_DENOM]: 10,
    [USDT_DENOM]: 10,
    [SOL_DENOM]: 0.2,
  },
  maxWin: {
    [INJ_DENOM]: 500,
    [NBZ_DENOM]: 100_000,
    [SHU_DENOM]: 10_000,
    [USDC_NEW_DENOM]: 2000,
    [USDT_DENOM]: 2000,
    [SOL_DENOM]: 50,
  },
}

export type GameState = {
  denom: string
  gameState: PlinkoState | null
}

export enum PlinkoState {
  INIT = 'INIT',
  READY = 'READY',
  GAMING = 'GAMING',
}

const initialState = {
  denom: INJ_DENOM,
  errors: [],
  isDirty: false,
  amount: '0.1',
  state: PlinkoState.INIT,
  minValue: currenciesSettings.minValue[INJ_DENOM],
  maxValue: currenciesSettings.maxValue[INJ_DENOM],
  maxWin: currenciesSettings.maxWin[INJ_DENOM],
}

export type Plinko = {
  denom: string
  amount: string
  minValue: number
  maxValue: number
  maxWin: number
  errors: string[]
  isDirty: boolean

  state: PlinkoState

  formRevalidation: () => void
  setDenom: (denom: string) => void
  setValues: (
    values: Partial<{
      [key in keyof Omit<Plinko, 'setValues' | 'formRevalidation'>]: { value: any; setDirty?: boolean }
    }>
  ) => void
}

export const usePlinkoStore = create<Plinko>((set, get) => ({
  formRevalidation: () => {
    const state = get()
    const { fields, errors } = formValidation({
      amount: state.amount,
      denom: state.denom,
      minValue: state.minValue,
      maxValue: state.maxValue,
    })
    set({
      errors,
      isDirty: true,
      ...fields,
    })
  },
  setDenom: (denom: string) => {
    const minValue = currenciesSettings.minValue[denom]

    set(() => {
      const { fields, errors } = formValidation({
        amount: (minValue * 10).toString(),
        denom: denom,
        minValue,
        maxValue: currenciesSettings.maxValue[denom],
      })

      return {
        errors,
        ...fields,
        isDirty: true,
        maxWin: currenciesSettings.maxWin[denom],
      }
    })
  },
  setValues: (values) => {
    set((state) => {
      let newFields = {
        amount: state.amount,
        denom: state.denom,
        minValue: state.minValue,
        maxValue: state.maxValue,
      }

      let isDirty = false
      for (const key in values) {
        newFields = {
          ...newFields,
          [key]: values[key as keyof typeof values]!.value,
        }

        isDirty = isDirty || values[key as keyof typeof values]!.setDirty !== false
      }

      const { fields, errors } = formValidation(formParser(newFields))

      sessionStorage.setItem('plinko-form', JSON.stringify(fields))

      return {
        errors,
        ...fields,
        isDirty: !isDirty ? state.isDirty : true,
      }
    })
  },
  ...initialState,
}))

const formParser = (values: FormFields) => {
  const { amount } = values
  const token = getTokenByDenom(values.denom)
  const fields = {
    amount,
    minValue: currenciesSettings.minValue[values.denom]!,
    maxValue: currenciesSettings.maxValue[values.denom]!,
  }

  if (isNaN(Number(amount)) || Number(amount) < 0) {
    fields.amount = ''
    return { ...values, ...fields }
  }

  if (Number(amount) > fields.maxValue) {
    fields.amount = fields.maxValue.toString()
  }

  const regex = new RegExp(`^\\d+(\\.\\d{0,${token.precisions}})?$`, 'g')

  if (amount != '' && !regex.test(amount)) {
    fields.amount = Number(amount).toFixed(token.precisions)
  }

  return { ...values, ...fields }
}

type FormFields = {
  amount: string
  denom: string
  minValue: number
  maxValue: number
}

const formValidation = (fields: FormFields) => {
  let { amount, minValue } = fields

  const errors = []

  if (amount.length === 0) {
    errors.push('Amount is required')
  }

  if (Number(amount) < minValue) {
    errors.push('Min amount is ' + minValue)
  }

  return {
    fields,
    errors,
  }
}
